import axiosClient from './axiosClient';

// Keep this list in sync with Laravel's upload validation.
export const SUPPORTED_3D_EXTENSIONS = ['glb'];

export const get3DFileExtension = (file) => {
  const name = file?.name || '';
  return name.includes('.') ? name.split('.').pop().toLowerCase() : '';
};

export const validate3DModelFile = (file) => {
  if (!file) return null;
  if (typeof File === 'undefined' || !(file instanceof File)) {
    return 'Please select a valid 3D model file.';
  }
  const extension = get3DFileExtension(file);
  if (!SUPPORTED_3D_EXTENSIONS.includes(extension)) {
    return 'Export your Blender design as .glb, then select the GLB file.';
  }
  if (file.size === 0) return 'The selected model file is empty.';
  if (file.size > 50 * 1024 * 1024) return 'The GLB file must be 50 MB or less.';
  return null;
};

const appendJsonField = (formData, key, value) => {
  if (value === undefined) return;
  formData.append(key, value === null ? '' : JSON.stringify(value));
};

export const buildProductFormData = (payload = {}, method = null) => {
  const formData = new FormData();
  if (method) formData.append('_method', method);

  [
    'name', 'product_type', 'access_type', 'nfc_chip_type', 'nfc_notes',
    'description', 'base_unit_price', 'minimum_quantity', 'sort_order',
    'preview_mode', 'three_d_source',
  ].forEach((key) => {
    if (payload[key] !== undefined && payload[key] !== null) {
      formData.append(key, payload[key]);
    }
  });

  [
    'is_recommended', 'supports_front_design', 'supports_back_design',
    'nfc_writable', 'nfc_lock_after_write', 'is_active', 'remove_three_d_model',
  ].forEach((key) => {
    if (payload[key] !== undefined) {
      formData.append(key, payload[key] ? '1' : '0');
    }
  });

  [
    'available_sizes', 'available_materials', 'available_colors',
    'customization_fields', 'editor_schema', 'recommendation_tags',
    'three_d_definition', 'three_d_settings',
  ].forEach((key) => appendJsonField(formData, key, payload[key]));

  if (typeof File !== 'undefined' && payload.main_image instanceof File) {
    formData.append('main_image', payload.main_image);
  }

  (payload.gallery_images || []).forEach((file) => {
    if (typeof File !== 'undefined' && file instanceof File) {
      formData.append('gallery_images[]', file);
    }
  });

  if (payload.remove_main_image !== undefined) {
    formData.append('remove_main_image', payload.remove_main_image ? '1' : '0');
  }
  (payload.remove_gallery_images || []).forEach((path) => {
    formData.append('remove_gallery_images[]', path);
  });

  if (payload.three_d_model != null) {
    const error = validate3DModelFile(payload.three_d_model);
    if (error) throw new Error(error);
    // Existing Laravel field name retained for backwards compatibility.
    // GLB is stored and served directly by Laravel; no conversion service.
    formData.append('three_d_model', payload.three_d_model, payload.three_d_model.name);
  }
  return formData;
};

export const qrPrintApi = {
  getProducts(params = {}) { return axiosClient.get('/qr-print/products', { params }); },
  getRecommendations() { return axiosClient.get('/qr-print/products/recommendations'); },
  getProduct(productId) { return axiosClient.get(`/qr-print/products/${productId}`); },
  getProductImage(productId, imageType, imageIndex = 0) {
    const type = imageType === 'main' ? 'main' : 'gallery';
    return axiosClient.get(`/qr-print/products/${productId}/images/${type}/${imageIndex}`, { responseType: 'blob' });
  },
  // Fetch the GLB with the same authentication and CORS handling as normal API calls.
  getProduct3DModelFile(productId) {
    return axiosClient.get(`/qr-print/products/${productId}/model-file`, { responseType: 'blob' });
  },
  createProduct(payload) {
    // Axios sets the multipart boundary automatically; do not hardcode Content-Type.
    return axiosClient.post('/qr-print/products', buildProductFormData(payload));
  },
  updateProduct(productId, payload) {
    return axiosClient.post(`/qr-print/products/${productId}`, buildProductFormData(payload, 'PUT'));
  },
  deleteProduct(productId) { return axiosClient.delete(`/qr-print/products/${productId}`); },

  // NEW API CONTRACT: implement GET /qr-print/products/{id}/model-status in Laravel.
  // Suggested response: { data: { status: 'pending'|'processing'|'ready'|'failed',
  //   three_d_model_url: null|string, error: null|string } }.
  // 'ready' must only be returned when the generated GLB is accessible.
  getProduct3DModelStatus(productId) {
    return axiosClient.get(`/qr-print/products/${productId}/model-status`);
  },

  getMenus(establishmentId, params = {}) { return axiosClient.get(`/establishments/${establishmentId}/menus`, { params }); },
  getMenu(menuId) { return axiosClient.get(`/menus/${menuId}`); },
  getMenuQrRecommendations(menuId) { return axiosClient.get(`/qr-print/menus/${menuId}/recommendations`); },
  analyzeAiProduct(productId, payload) { return axiosClient.post(`/qr-print/products/${productId}/ai-customize/analyze`, payload); },
  generateAiCustomization(productId, payload) { return axiosClient.post(`/qr-print/products/${productId}/ai-customize/generate`, payload); },
  getAiCustomization(customizationId) { return axiosClient.get(`/qr-print/ai-customizations/${customizationId}`); },
  acceptAiCustomization(customizationId) { return axiosClient.post(`/qr-print/ai-customizations/${customizationId}/accept`); },
  startRecommendationBatch(menuId) { return axiosClient.post(`/qr-print/menus/${menuId}/recommendation-batches`); },
  getRecommendationBatch(batchId) { return axiosClient.get(`/qr-print/recommendation-batches/${batchId}`); },
  getDesigns(params = {}) { return axiosClient.get('/qr-print/designs', { params }); },
  getDesign(designId) { return axiosClient.get(`/qr-print/designs/${designId}`); },
  createDesign(payload) { return axiosClient.post('/qr-print/designs', payload); },
  updateDesign(designId, payload) { return axiosClient.put(`/qr-print/designs/${designId}`, payload); },
  deleteDesign(designId) { return axiosClient.delete(`/qr-print/designs/${designId}`); },
  getCart() { return axiosClient.get('/qr-print/cart'); },
  addToCart(payload) { return axiosClient.post('/qr-print/cart', payload); },
  updateCartItem(itemId, payload) { return axiosClient.patch(`/qr-print/cart/${itemId}`, payload); },
  removeCartItem(itemId) { return axiosClient.delete(`/qr-print/cart/${itemId}`); },
  getOrders(params = {}) { return axiosClient.get('/qr-print/orders', { params }); },
  getOrder(orderId) { return axiosClient.get(`/qr-print/orders/${orderId}`); },
  createOrder(payload) { return axiosClient.post('/qr-print/orders', payload); },
  createQuote(orderId, payload) { return axiosClient.post(`/qr-print/orders/${orderId}/quotes`, payload); },
  acceptQuote(orderId, quoteId) { return axiosClient.post(`/qr-print/orders/${orderId}/quotes/${quoteId}/accept`); },
  updateOrderStatus(orderId, payload) { return axiosClient.patch(`/qr-print/orders/${orderId}/status`, payload); },
  cancelOrder(orderId) { return axiosClient.post(`/qr-print/orders/${orderId}/cancel`); },
};

export default qrPrintApi;
